import { useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import Spinner from "components/base/Spinner";
import { useModals } from "ModalProvider";
import { useState } from "react";
import { FETCH_ODOO_ORDERS_FOR_FSI } from "./graphql";
import NewOrdersForm from "./NewOrdersForm";

const OdooOrdersViewForFSIView = ({ fsiOrderDate }) => {
  const { loading, error, data } = useQuery(FETCH_ODOO_ORDERS_FOR_FSI, {
    variables: { fsiOrderDate },
    fetchPolicy: "network-only",
  });

  const [selectedOrders, setSelectedOrders] = useState([]);
  const modal = useModals();

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const res = JSON.parse(data.odooSaleOrders).records;

  function trySend() {
    const orders = selectedOrders.map((id) =>
      res.find((order) => order.id === id),
    );

    const formattedOrders = orders.map((i) => {
      const lineItems = i.order_line
        .filter((line) => line.product_id.is_storable)
        .map((line) => ({
          itemNumber: line.product_id.default_code,
          quantity: line.product_uom_qty,
        }));

      return {
        orderNumber: i.name,
        alternateOrderNumber: i.origin,
        orderDate: i.date_order,
        shippingCode: "FCO",
        shippingAddress: {
          name: i["partner_shipping_id"]["display_name"],
          contact: "",
          address1: i["partner_shipping_id"]["street"],
          address2: i["partner_shipping_id"]["street2"] || "",
          city: i["partner_shipping_id"]["city"],
          state: i["partner_shipping_id"]["state_id"]["code"],
          postalCode: i["partner_shipping_id"]["zip"],
          countryCode: i["partner_shipping_id"]["country_code"],
          phone: i["partner_shipping_id"]["phone"] || "",
          email: i["partner_shipping_id"]["email"] || "",
        },
        billingAddress: {
          name: i["partner_invoice_id"]["display_name"],
          contact: "",
          address1: i["partner_invoice_id"]["street"],
          address2: i["partner_invoice_id"]["street2"] || "",
          city: i["partner_invoice_id"]["city"],
          state: i["partner_invoice_id"]["state_id"]["code"],
          postalCode: i["partner_invoice_id"]["zip"],
          countryCode: i["partner_invoice_id"]["country_code"],
          phone: i["partner_invoice_id"]["phone"] || "",
          email: i["partner_invoice_id"]["email"] || "",
        },
        lineItems: lineItems,
        tradingPartner: "WHOLE",
        orderMessage: "",
      };
    });

    modal.present({
      title: "Send to FSI",
      maxWidth: "max-w-5xl",
      children: (
        <NewOrdersForm initialOrders={formattedOrders} hide={modal.hide} />
      ),
    });
  }

  if (res.length === 0)
    return (
      <div className="p-6 opacity-70">
        No orders found for the selected date.
      </div>
    );

  return (
    <table>
      <thead>
        <tr className="z-10 sticky top-0 bg-gray-50 dark:bg-gray-700">
          <th className="w-6 px-4 py-3">
            <input
              type="checkbox"
              checked={selectedOrders.length === res.length && res.length > 0}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedOrders(res.map((order) => order.id));
                } else {
                  setSelectedOrders([]);
                }
              }}
            />
          </th>
          {selectedOrders.length > 0 ? (
            <>
              <th colSpan={5}>
                <div className="flex items-center space-x-3">
                  <span>{selectedOrders.length} selected orders</span>
                  <Button onClick={trySend}>Send to FSI</Button>
                </div>
              </th>
            </>
          ) : (
            <>
              <th>Number</th>
              <th>Order Date</th>
              <th>Customer</th>
              <th className="text-right">Amount</th>
              <th className="text-right px-4">Status</th>
            </>
          )}
        </tr>
      </thead>
      <tbody>
        {res.map((order) => (
          <tr
            key={order.id}
            className="border-y border-gray-100 dark:border-gray-700"
          >
            <td className="px-4">
              <input
                type="checkbox"
                checked={selectedOrders.includes(order.id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedOrders([...selectedOrders, order.id]);
                  } else {
                    setSelectedOrders(
                      selectedOrders.filter((id) => id !== order.id),
                    );
                  }
                }}
              />
            </td>
            <td>{order.name}</td>
            <td>{order.date_order}</td>
            <td>{order.partner_id?.display_name}</td>
            <td className="text-right">{order.amount_total}</td>
            <td className="text-right px-4">{order.state}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default OdooOrdersViewForFSIView;
